/**
 * Auth context (spec §4.1). One react-query entry (`["auth","me"]`) is the
 * single source of truth for "who is signed in"; mutations invalidate it.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import type { UserPrefsInput } from "@shared/userPrefs";
import type { Lang, SessionUser } from "@shared/api";
import { ApiError, api } from "@client/api/client";
import { clearAllExamDrafts } from "@client/features/exam/draft";
import { clearAllGradeDrafts } from "@client/features/exam/gradeDraft";
import { clearAllReflectionDrafts } from "@client/features/exam/wrapper/draft";
import { clearSession as clearFlashcardsSession } from "@client/features/flashcards/session";
import { clearSession as clearPracticeSession } from "@client/features/practice/session";
import { clearDrafts as clearSelfExplanations } from "@client/features/practice/selfExplanationStore";
import { clearPlanSet } from "@client/features/practice/sets/planSet";
import { clearTodaySettings } from "@client/features/practice/today/settings";
import { clearRunner as clearTodayRunner } from "@client/features/practice/today/session";
import { useLang } from "@client/i18n";
import { setStorageOwner } from "@client/lib/storageOwner";

/**
 * What a sign-out wipes from this browser, so the next account on the same device cannot resume or
 * read the previous student's work. The practice set lives in `sessionStorage` (this tab) and
 * `localStorage` (`mf.practice.session`, 7 days, stamped with the user id); Dagens pass's runner
 * (`mf.today.session`) is in `localStorage` with the same stamp. The flashcard session, the
 * "Vilken princip?" set (`mf.practice.planSet`), the self-explanation drafts and Today's own
 * choices (`mf.today.settings`, `mf.today.skipTopics`) are wiped too, as are the exam's
 * self-grading ticks (`mf.exam.grade.<sessionId>`), answer drafts (`mf.exam.session.<id>`) and
 * the result page's reflection drafts and "Dölj" flags (`mf.exam.reflection.<id>`,
 * `mf.exam.lastPlan.hidden.<id>`). Device preferences (`mf.theme`, `mf.lang`,
 * `mf.practice.showAllParts`) stay.
 */
function clearFeatureSessions(): void {
  clearPracticeSession();
  clearFlashcardsSession();
  clearPlanSet();
  clearSelfExplanations();
  clearTodayRunner();
  clearTodaySettings();
  clearAllGradeDrafts();
  clearAllExamDrafts();
  clearAllReflectionDrafts();
}

export const ME_QUERY_KEY = ["auth", "me"] as const;

/** `GET /api/auth/providers` (spec §2). `google` is false only via the kill switch. */
export interface AuthProviders {
  google: boolean;
  teacherCodeEnabled: boolean;
}

export const PROVIDERS_QUERY_KEY = ["auth", "providers"] as const;

export interface UserPatch {
  displayName?: string;
  language?: Lang;
  newCardsPerDay?: number;
  bonusPoints?: number;
  /** The "Kom igång" answers (slice C); the server stamps the version and the time. */
  prefs?: UserPrefsInput;
}

export interface AuthValue {
  user: SessionUser | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
  updateMe: (patch: UserPatch) => Promise<SessionUser>;
  /** No password: the server allows it for a session younger than 10 minutes (spec §7.4). */
  deleteMe: () => Promise<void>;
  /** Switches the UI language and, when signed in, persists it on the profile. */
  setLanguage: (lang: Lang) => void;
}

const AuthContext = createContext<AuthValue | null>(null);

async function fetchMe(): Promise<SessionUser | null> {
  try {
    const { user } = await api.get<{ user: SessionUser }>("/api/auth/me");
    return user;
  } catch (error) {
    if (error instanceof ApiError && error.code === "UNAUTHENTICATED") return null;
    throw error;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { lang, setLang } = useLang();

  const { data, isLoading } = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: fetchMe,
    staleTime: 60_000,
    retry: false,
  });
  const user = data ?? null;
  // Before any child renders: stored drafts check their owner against it.
  setStorageOwner(user?.id ?? null);

  const patchMutation = useMutation({
    mutationFn: (patch: UserPatch) => api.patch<{ user: SessionUser }>("/api/auth/me", patch),
  });

  // Adopt the profile language once per signed-in user.
  const syncedFor = useRef<string | null>(null);
  // The language we last asked the server to hold. `setLanguage` compares
  // against this — not `user.language` — since the patch mutation used to
  // never update the cached user, leaving `user.language` stale after the
  // first toggle and causing a toggle back to be silently skipped.
  const targetLanguage = useRef<Lang | null>(null);
  useEffect(() => {
    if (user && syncedFor.current !== user.id) {
      syncedFor.current = user.id;
      targetLanguage.current = user.language;
      if (user.language !== lang) setLang(user.language);
    }
    if (!user) {
      syncedFor.current = null;
      targetLanguage.current = null;
    }
  }, [user, lang, setLang]);

  const value = useMemo<AuthValue>(() => {
    const setUser = (next: SessionUser | null) => {
      queryClient.setQueryData(ME_QUERY_KEY, next);
    };
    return {
      user,
      isLoading,
      signOut: async () => {
        await api.post("/api/auth/logout");
        setUser(null);
        queryClient.clear();
        clearFeatureSessions();
      },
      updateMe: async (patch) => {
        const { user: updated } = await patchMutation.mutateAsync(patch);
        setUser(updated);
        return updated;
      },
      deleteMe: async () => {
        await api.delete("/api/auth/me");
        setUser(null);
        queryClient.clear();
        clearFeatureSessions();
      },
      setLanguage: (next) => {
        setLang(next);
        if (!user || targetLanguage.current === next) return;
        // Record the intent synchronously (before the PATCH resolves) so a
        // rapid follow-up toggle compares against it rather than the stale
        // value still in the cache.
        targetLanguage.current = next;
        patchMutation.mutate(
          { language: next },
          {
            onSuccess: (data) => {
              // A newer toggle may have moved the target on since this
              // request went out; only the response matching the current
              // target should win, so an out-of-order response can't
              // clobber a later choice — last write (by intent) wins.
              if (targetLanguage.current === next) setUser(data.user);
            },
          },
        );
      },
    };
  }, [user, isLoading, queryClient, patchMutation, setLang]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** Convenience for pages that only care about the role. */
export function useIsTeacher(): boolean {
  return useAuth().user?.role === "teacher";
}

/** Which sign-in options the server offers (spec §2); shared by Login and the Google pages. */
export function useAuthProviders(): UseQueryResult<AuthProviders> {
  return useQuery({
    queryKey: PROVIDERS_QUERY_KEY,
    queryFn: () => api.get<AuthProviders>("/api/auth/providers"),
    staleTime: 5 * 60_000,
    retry: false,
  });
}
